import { getLocalDate } from '@surfline/web-common';
import type { ConditionRating } from 'types/conditionRating';
import type { SurfData, SurfTimestep } from 'types/surf';
import type { WindTimestep, WindData } from 'types/wind';
import { formatTimestampInTimezone } from 'utils/time';

export const getDaySelectorData = (
  ratingsDataDays: Array<ConditionRating[]> | undefined,
  surfDataDays: Array<SurfTimestep[]>,
  windDataDays: Array<Array<WindTimestep>>,
) => {
  const result = surfDataDays?.map((surfDay: Array<SurfTimestep>, dayIndex) => {
    let am: { key: null | string } = { key: null };
    let midday: { key: null | string } = { key: null };
    let pm: { key: null | string } = { key: null };
    let min = 0;
    let max = 0;
    let plus = false;
    let date = null;
    let morningWind: WindTimestep | null = null;
    let noonWind: WindTimestep | null = null;
    let eveningWind: WindTimestep | null = null;

    surfDay?.forEach((hour: any, hourIndex: number) => {
      if (!hour) return null;

      const formattedHour = formatTimestampInTimezone(hour.timestamp, hour.utcOffset, 'haaa');
      if (formattedHour === '12pm') {
        max = hour.surf.max;
        min = hour.surf.min;
        plus = hour.surf.plus;
        // use the noon data point for the current date to handle dst shifts
        date = getLocalDate(hour.timestamp, hour.utcOffset);
      }

      if (ratingsDataDays?.[dayIndex]?.[hourIndex]) {
        if (formattedHour === '6am')
          am = { key: ratingsDataDays[dayIndex][hourIndex].rating || '' };
        if (formattedHour === '12pm')
          midday = { key: ratingsDataDays[dayIndex][hourIndex].rating || '' };
        if (formattedHour === '6pm')
          pm = { key: ratingsDataDays[dayIndex][hourIndex].rating || '' };
      }

      if (windDataDays?.[dayIndex]?.[hourIndex]) {
        if (formattedHour === '6am') morningWind = windDataDays[dayIndex][hourIndex];
        if (formattedHour === '12pm') noonWind = windDataDays[dayIndex][hourIndex];
        if (formattedHour === '6pm') eveningWind = windDataDays[dayIndex][hourIndex];
      }

      return null;
    });

    return {
      date,
      surf: {
        min,
        max,
        plus,
      },
      rating: {
        am,
        midday,
        pm,
      },
      wind: {
        am: morningWind,
        midday: noonWind,
        pm: eveningWind,
      },
    };
  });

  return result || null;
};

export const getSurfHeightsForDays = (surfDays: Array<SurfTimestep>) =>
  surfDays
    ? surfDays.map((hour) => ({
        min: hour.surf.min,
        max: hour.surf.max,
        timestamp: hour.timestamp,
        utcOffset: hour.utcOffset,
      }))
    : null;

export const daySelectorDataTransformer = ({
  ratings,
  spotId,
  surf,
  wind,
}: {
  ratings: { days: Array<ConditionRating[]> };
  spotId: string;
  surf: SurfData;
  wind: WindData;
}) => ({
  associated: {
    units: {
      waveHeight: surf?.units?.waveHeight,
    },
  },
  data: {
    overview: [
      {
        days: getDaySelectorData(ratings?.days, surf?.days, wind?.days),
        spotId,
        surf: getSurfHeightsForDays(surf?.days?.flat()),
      },
    ],
  },
});

export default daySelectorDataTransformer;
